import { FC, useEffect, useState } from 'react';

import { getImagePathsOfThumbs } from '@utils_iqfu/getImagePathsOfThumbs';

import styles from './DisplayThumbImage.module.scss';

type Props = {
  originalImagePath: string;
  thumbSize: 'large' | 'medium' | 'icon';
  lazyLoading?: boolean;
  fetchPriority?: 'high' | 'low' | 'auto';
  altText?: string;
  companyCard?: boolean;
};

const { getLargeThumbPath, getMediumThumbPath, getIconPath } = getImagePathsOfThumbs;

const getImagePath = (size: 'large' | 'medium' | 'icon' | undefined, originalImagePath: string) => {
  switch (size) {
    case 'large':
      return getLargeThumbPath(originalImagePath);
    case 'medium':
      return getMediumThumbPath(originalImagePath);
    case 'icon':
      return getIconPath(originalImagePath);
    default:
      return undefined;
  }
};

const fallBackImagePath = '/images/iqfu/public/no_company_image.png';

const DisplayThumbImage: FC<Props> = ({
  originalImagePath,
  thumbSize,
  lazyLoading = false,
  fetchPriority = 'auto',
  altText = '',
  companyCard = false,
}) => {
  const [hasNoImage, setHasNoImage] = useState(false);
  const [displayImage, setDisplayImage] = useState<string | undefined>(getImagePath(thumbSize, originalImagePath));

  useEffect(() => {
    const newImagePath = getImagePath(thumbSize, originalImagePath);
    setDisplayImage(newImagePath);
    setHasNoImage(newImagePath === fallBackImagePath || !newImagePath);
  }, [originalImagePath, thumbSize]);

  const handleError = ({ currentTarget }: React.SyntheticEvent<HTMLImageElement, Event>) => {
    currentTarget.onerror = null;
    setDisplayImage(fallBackImagePath);
    setHasNoImage(true);
  };

  return (
    <>
      {originalImagePath && (
        <span className={`${styles.thumb_srcset} ${hasNoImage ? styles.show_fallback_image : ''}`}>
          <img
            src={displayImage}
            onError={handleError}
            loading={lazyLoading ? 'lazy' : 'eager'}
            fetchpriority={fetchPriority}
            alt={altText}
            data-gtm-click={companyCard ? 'DisplayThumbImage_img' : ''}
          />
        </span>
      )}
    </>
  );
};

export default DisplayThumbImage;
